import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Building2, Baby, UserCheck, Clock, CheckCircle, RefreshCw, Calculator } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to calculate age from date string
const calculateAge = (dateString) => {
  if (!dateString) return null;
  const today = new Date();
  const birthDate = new Date(dateString);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

export const Register = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('register'); // 'register' | 'pending'
  const [clubs, setClubs] = useState([]);
  const [loadingClubs, setLoadingClubs] = useState(true);
  const [mounted, setMounted] = useState(false);
  
  // Captcha state
  const [captcha, setCaptcha] = useState({ session_id: '', question: '' });
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [loadingCaptcha, setLoadingCaptcha] = useState(true);
  
  const [formData, setFormData] = useState({
    email: '',
    nom: '',
    prenom: '',
    telephone: '',
    date_naissance: '',
    club_id: '',
    // Parent/Guardian info for minors
    parent_nom: '',
    parent_prenom: '',
    parent_email: '',
    parent_telephone: '',
    parent_lien: 'parent',
    autorisation_parentale: false,
    notifications_parent: 'both',
    // Honeypot field (should remain empty)
    website: ''
  });
  const [loading, setLoading] = useState(false);
  
  // Calculate if user is minor based on birth date
  const isMinor = useMemo(() => {
    const age = calculateAge(formData.date_naissance);
    return age !== null && age < 18;
  }, [formData.date_naissance]);
  
  const userAge = useMemo(() => calculateAge(formData.date_naissance), [formData.date_naissance]);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Fetch available clubs and captcha on mount
  useEffect(() => {
    const fetchClubs = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/auth/clubs`);
        setClubs(response.data);
      } catch (error) {
        console.error('Error fetching clubs:', error);
        toast.error('Erreur lors du chargement des clubs');
      } finally {
        setLoadingClubs(false);
      }
    };
    fetchClubs();
    fetchCaptcha();
  }, []);

  const fetchCaptcha = async () => {
    setLoadingCaptcha(true);
    try {
      const response = await axios.get(`${API_URL}/api/auth/captcha`);
      setCaptcha(response.data);
      setCaptchaAnswer('');
    } catch (error) {
      console.error('Error fetching captcha:', error);
    } finally {
      setLoadingCaptcha(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.club_id) {
      toast.error('Veuillez sélectionner votre club');
      return;
    }
    
    if (!formData.date_naissance) {
      toast.error('La date de naissance est obligatoire');
      return;
    }
    
    if (!captchaAnswer) {
      toast.error('Veuillez répondre à la question anti-spam');
      return;
    }
    
    // Validation for minors
    if (isMinor) {
      if (!formData.autorisation_parentale) {
        toast.error('L\'autorisation parentale est obligatoire pour les mineurs');
        return;
      }
      if (!formData.parent_nom || !formData.parent_prenom || !formData.parent_telephone) {
        toast.error('Les informations du parent/tuteur sont obligatoires pour les mineurs');
        return;
      }
    }
    
    setLoading(true);
    
    try {
      // Prepare request data
      const requestData = {
        email: formData.email,
        nom: formData.nom,
        prenom: formData.prenom,
        telephone: formData.telephone || null,
        date_naissance: formData.date_naissance,
        club_id: formData.club_id,
        // Anti-spam
        captcha_session_id: captcha.session_id,
        captcha_answer: parseInt(captchaAnswer, 10),
        honeypot: formData.website || null  // Should be empty
      };
      
      // Add parent info if minor
      if (isMinor) {
        requestData.parent_info = {
          nom: formData.parent_nom,
          prenom: formData.parent_prenom,
          email: formData.parent_email || null,
          telephone: formData.parent_telephone,
          lien: formData.parent_lien
        };
        requestData.autorisation_parentale = formData.autorisation_parentale;
        requestData.notifications_parent = formData.notifications_parent;
      }
      
      const response = await axios.post(`${API_URL}/api/auth/register`, requestData);
      
      toast.success(response.data.message || 'Demande d\'inscription envoyée');
      setStep('pending');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'inscription';
      toast.error(message);
      // Refresh captcha on error
      fetchCaptcha();
    } finally {
      setLoading(false);
    }
  };

  // Common input style for dark theme
  const inputClass = "bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-white/50 focus:ring-white/20";
  const labelClass = "text-white/80 text-sm font-medium";

  return (
    <div className="min-h-screen bg-[#2E4A3D] flex flex-col items-center px-4 py-8 overflow-y-auto">
      {/* Back button */}
      <button
        onClick={() => step === 'pending' ? setStep('register') : navigate('/')}
        className="absolute top-6 left-6 flex items-center gap-2 text-white/70 hover:text-white transition-colors z-20"
        data-testid="back-btn"
      >
        <ArrowLeft size={20} />
        <span className="hidden sm:inline">{step === 'pending' ? 'Modifier' : 'Retour'}</span>
      </button>

      {/* Logo + Equibook text - morphed from landing page (2x smaller) */}
      <div 
        className={`flex flex-col items-center transition-all duration-700 ease-out mt-8 sm:mt-4 ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'
        }`}
      >
        <img 
          src="/logo-equibook.svg" 
          alt="Equibook" 
          className="w-20 h-20 sm:w-28 sm:h-28 md:w-36 md:h-36 object-contain transition-all duration-700"
          style={{ filter: 'drop-shadow(0 15px 30px rgba(0,0,0,0.3))' }}
        />
        
        {/* Brand name - justified to logo width */}
        <h1 className="text-xl sm:text-2xl md:text-3xl font-serif font-bold text-white mb-1 w-20 sm:w-28 md:w-36 flex justify-between mt-2">
          <span>E</span><span>q</span><span>u</span><span>i</span><span>b</span><span>o</span><span>o</span><span>k</span>
        </h1>
      </div>

      {/* Form card - appears with animation */}
      <div 
        className={`w-full max-w-md mt-4 sm:mt-6 transition-all duration-700 delay-200 ease-out ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 sm:p-6 border border-white/20 shadow-2xl">
          
          {step === 'register' ? (
            <>
              <div className="text-center mb-5">
                <h2 className="text-xl sm:text-2xl font-serif font-semibold text-white">Créer un compte</h2>
                <p className="text-white/60 text-sm mt-1">Remplissez le formulaire pour vous inscrire</p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Honeypot field - hidden from users, visible to bots */}
                <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
                  <Input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="prenom" className={labelClass}>Prénom *</Label>
                    <Input
                      id="prenom"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleChange}
                      placeholder="Jean"
                      className={`mt-1 ${inputClass}`}
                      data-testid="register-prenom"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="nom" className={labelClass}>Nom *</Label>
                    <Input
                      id="nom"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      placeholder="Dupont"
                      className={`mt-1 ${inputClass}`}
                      data-testid="register-nom"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="email" className={labelClass}>Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="votre@email.com"
                    className={`mt-1 ${inputClass}`}
                    data-testid="register-email"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="telephone" className={labelClass}>Téléphone mobile *</Label>
                  <Input
                    id="telephone"
                    name="telephone"
                    type="tel"
                    value={formData.telephone}
                    onChange={handleChange}
                    placeholder="06 12 34 56 78"
                    className={`mt-1 ${inputClass}`}
                    data-testid="register-telephone"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="date_naissance" className={labelClass}>Date de naissance *</Label>
                  <Input
                    id="date_naissance"
                    name="date_naissance"
                    type="date"
                    value={formData.date_naissance}
                    onChange={handleChange}
                    className={`mt-1 ${inputClass}`}
                    data-testid="register-dob"
                    required
                  />
                  {userAge !== null && (
                    <p className={`text-xs mt-1 ${isMinor ? 'text-amber-300' : 'text-white/50'}`}>
                      {isMinor ? (
                        <span className="flex items-center gap-1">
                          <Baby className="w-3 h-3" />
                          Cavalier mineur ({userAge} ans) - Informations du parent/tuteur requises
                        </span>
                      ) : (
                        `${userAge} ans`
                      )}
                    </p>
                  )}
                </div>
                
                {/* Parent/Guardian section for minors */}
                {isMinor && (
                  <div className="bg-amber-500/20 border border-amber-400/30 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-2 text-amber-300 font-medium text-sm">
                      <UserCheck className="w-4 h-4" />
                      <span>Informations du parent/tuteur</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label htmlFor="parent_prenom" className="text-amber-200/80 text-xs">Prénom *</Label>
                        <Input
                          id="parent_prenom"
                          name="parent_prenom"
                          value={formData.parent_prenom}
                          onChange={handleChange}
                          placeholder="Prénom"
                          className={`mt-1 text-sm ${inputClass}`}
                          data-testid="register-parent-prenom"
                          required={isMinor}
                        />
                      </div>
                      <div>
                        <Label htmlFor="parent_nom" className="text-amber-200/80 text-xs">Nom *</Label>
                        <Input
                          id="parent_nom"
                          name="parent_nom"
                          value={formData.parent_nom}
                          onChange={handleChange}
                          placeholder="Nom"
                          className={`mt-1 text-sm ${inputClass}`}
                          data-testid="register-parent-nom"
                          required={isMinor}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="parent_lien" className="text-amber-200/80 text-xs">Lien *</Label>
                      <Select
                        value={formData.parent_lien}
                        onValueChange={(value) => setFormData({ ...formData, parent_lien: value })}
                      >
                        <SelectTrigger className={`mt-1 text-sm ${inputClass}`} data-testid="register-parent-lien">
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="parent">Parent</SelectItem>
                          <SelectItem value="tuteur">Tuteur légal</SelectItem>
                          <SelectItem value="autre">Autre responsable</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="parent_telephone" className="text-amber-200/80 text-xs">Téléphone *</Label>
                      <Input
                        id="parent_telephone"
                        name="parent_telephone"
                        type="tel"
                        value={formData.parent_telephone}
                        onChange={handleChange}
                        placeholder="06 12 34 56 78"
                        className={`mt-1 text-sm ${inputClass}`}
                        data-testid="register-parent-telephone"
                        required={isMinor}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="parent_email" className="text-amber-200/80 text-xs">Email (optionnel)</Label>
                      <Input
                        id="parent_email"
                        name="parent_email"
                        type="email"
                        value={formData.parent_email}
                        onChange={handleChange}
                        placeholder="parent@email.com"
                        className={`mt-1 text-sm ${inputClass}`}
                        data-testid="register-parent-email"
                      />
                    </div>
                    
                    {/* Parental Authorization Checkbox */}
                    <div className="flex items-start gap-2 p-2 bg-white/10 rounded-lg">
                      <Checkbox
                        id="autorisation-parentale"
                        checked={formData.autorisation_parentale}
                        onCheckedChange={(checked) => setFormData({ ...formData, autorisation_parentale: checked })}
                        className="mt-0.5 border-amber-300"
                        data-testid="register-autorisation-parentale"
                      />
                      <div className="flex-1">
                        <Label htmlFor="autorisation-parentale" className="text-xs text-amber-200 cursor-pointer font-medium">
                          Autorisation parentale *
                        </Label>
                        <p className="text-xs text-amber-300/70 mt-0.5">
                          Je certifie être le représentant légal et j'autorise l'inscription.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div>
                  <Label htmlFor="club" className={labelClass}>Mon club *</Label>
                  <Select
                    value={formData.club_id}
                    onValueChange={(value) => setFormData({ ...formData, club_id: value })}
                    disabled={loadingClubs}
                  >
                    <SelectTrigger className={`mt-1 ${inputClass}`} data-testid="register-club">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-white/40" />
                        <SelectValue placeholder={loadingClubs ? "Chargement..." : "Choisir mon club"} />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {clubs.map(club => (
                        <SelectItem key={club.id} value={club.id}>
                          {club.nom} {club.ville && `(${club.ville})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Captcha */}
                <div className="bg-blue-500/20 border border-blue-400/30 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-blue-300 font-medium text-sm mb-2">
                    <Calculator className="w-4 h-4" />
                    <span>Vérification anti-spam</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Label className="text-blue-200/80 text-xs">{captcha.question || 'Chargement...'}</Label>
                      <Input
                        type="number"
                        value={captchaAnswer}
                        onChange={(e) => setCaptchaAnswer(e.target.value)}
                        placeholder="Réponse"
                        className={`mt-1 text-sm ${inputClass}`}
                        data-testid="register-captcha"
                        disabled={loadingCaptcha}
                        required
                      />
                    </div>
                    <button
                      type="button"
                      onClick={fetchCaptcha}
                      className="p-2 text-blue-300 hover:text-blue-100 hover:bg-blue-500/20 rounded-lg transition-colors"
                      title="Nouvelle question"
                      disabled={loadingCaptcha}
                    >
                      <RefreshCw className={`w-4 h-4 ${loadingCaptcha ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                </div>
                
                {/* Info box */}
                <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-xs text-white/70">
                  <div className="flex items-start gap-2">
                    <Clock className="w-4 h-4 mt-0.5 text-white/50 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-white/80">Prochaines étapes :</p>
                      <ol className="list-decimal list-inside space-y-0.5 mt-1">
                        <li>Examen par l'instructeur</li>
                        <li>Délai : <strong>72h max</strong></li>
                        <li>Email d'activation à l'approbation</li>
                      </ol>
                    </div>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  className="w-full py-2.5 bg-white text-[#2E4A3D] hover:bg-white/90 font-semibold rounded-full transition-all shadow-lg hover:shadow-xl"
                  disabled={loading}
                  data-testid="register-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    'Envoyer ma demande'
                  )}
                </Button>
              </form>
            </>
          ) : (
            /* Pending Step - Account created, awaiting approval */
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-amber-300" />
              </div>
              
              <h2 className="text-xl sm:text-2xl font-serif font-semibold text-white mb-2">
                Demande envoyée !
              </h2>
              <p className="text-white/70 text-sm mb-4">
                Inscription au club <strong className="text-white">{clubs.find(c => c.id === formData.club_id)?.nom || 'sélectionné'}</strong> transmise.
              </p>
              
              <div className="bg-amber-500/20 border border-amber-400/30 rounded-lg p-3 mb-3 text-left">
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-amber-300 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-amber-200 font-medium text-sm">En attente de validation</p>
                    <p className="text-amber-300/80 text-xs mt-1">
                      Délai maximum : <strong>72 heures</strong>
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-500/20 border border-blue-400/30 rounded-lg p-3 mb-3 text-left">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-blue-300 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-blue-200 font-medium text-sm">Email de confirmation envoyé</p>
                    <p className="text-blue-300/80 text-xs mt-1">
                      Envoyé à <strong className="text-blue-200">{formData.email}</strong>
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-green-500/20 border border-green-400/30 rounded-lg p-3 mb-4 text-left">
                <p className="text-green-200 font-medium text-sm mb-1">À l'approbation :</p>
                <p className="text-green-300/80 text-xs">
                  Lien d'activation par email (valable 72h) pour créer votre mot de passe.
                </p>
              </div>
              
              <Button
                onClick={() => navigate('/login')}
                className="w-full py-2.5 bg-white text-[#2E4A3D] hover:bg-white/90 font-semibold rounded-full transition-all shadow-lg hover:shadow-xl"
                data-testid="go-to-login"
              >
                Retour à la connexion
              </Button>
            </div>
          )}
          
          <div className="mt-4 pt-3 border-t border-white/10 text-center">
            <p className="text-white/60 text-sm">
              Déjà un compte ?{' '}
              <Link to="/login" className="text-white font-medium hover:underline">
                Se connecter
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
